import { Microfrontend } from '@cian/mf-react';
import { useBooleanState } from '@cian/react-utils';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { useNewbuildingBrokerWidgetAPI } from './useNewbuildingBrokerWidgetAPI';
import { NewbuildingBrokerWidgetContext } from './NewbuildingBrokerWidgetContext';
import { TOpenWidgetResultHandler } from './types';
import { BrokerFallbackModal } from '../../components/BrokerFallbackModal';
import { getCurrentJsonQuery } from '../../selectors/location';
import { IOpenBrokerModalParams } from '../../types/microfrontends/newbuildingBrokerWidget';
import { IBrokerAnalyticsUser } from '../../types/broker';
import { trackFallbackModalOpen, trackFallbackModalRetryClick } from '../../utils/newbuildingBroker/sharedTracking';

/**
 * Компонент, обеспечивающий загрузку микрофронта брокерских модалок на клиенте.
 *
 * 1. Пользователь кликает на кнопку, и вызывается `handleWidgetOpeningStart`.
 * 2. Регистрируем манифест, загружаем ассеты
 * 3. Сеттим в React-стейт манифест. Ждем сеттинга с помощью useEffect.
 * 4. По стейту отрисовывается <Microfrontend />
 * 5. Инициализуруется реф на <Microfrontend />, содержащий локальное API.
 * 6. Вызываем на рефе локальное API, и открывается модалка брокерки.
 */
export const NewbuildingBrokerWidgetProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const paramsRef = React.useRef<IOpenBrokerModalParams>();
  const [loading, setLoading] = React.useState(false);
  const { state: isFallbackModalVisible, setTrue: showFallbackModal, setFalse: closeFallbackModal } = useBooleanState();
  const jsonQuery = useSelector(getCurrentJsonQuery);

  const handleWidgetOpeningFinish = React.useCallback<TOpenWidgetResultHandler>(
    ({ isError }) => {
      if (isError) {
        const user = paramsRef.current?.trackingParams?.user as IBrokerAnalyticsUser;

        if (!isFallbackModalVisible) {
          trackFallbackModalOpen(jsonQuery, user);
          showFallbackModal();
        }
      } else if (isFallbackModalVisible && !isError) {
        closeFallbackModal();
      }
    },
    [closeFallbackModal, jsonQuery, isFallbackModalVisible, showFallbackModal],
  );

  const { openWidget, widgetRef, widgetManifest } = useNewbuildingBrokerWidgetAPI(handleWidgetOpeningFinish);

  const handleWidgetOpeningStart = React.useCallback(
    async (params: IOpenBrokerModalParams) => {
      // Сохраняем в реф для работы с теми же параметрами на случай, если произойдет ошибка.
      paramsRef.current = params;

      await openWidget(params);
    },
    [openWidget],
  );

  const handleRetryClick = React.useCallback(async () => {
    const user = paramsRef.current?.trackingParams?.user as IBrokerAnalyticsUser;
    trackFallbackModalRetryClick(jsonQuery, user);

    setLoading(true);
    await openWidget(paramsRef.current as IOpenBrokerModalParams);
    setLoading(false);
  }, [jsonQuery, openWidget]);

  return (
    <NewbuildingBrokerWidgetContext.Provider value={handleWidgetOpeningStart}>
      {children}
      {widgetManifest && (
        <Microfrontend
          ref={widgetRef}
          name={widgetManifest.microfrontendName}
          image={widgetManifest.imageVersion}
          runtime={widgetManifest.runtimeName}
          instance={widgetManifest.instanceId}
        />
      )}
      {isFallbackModalVisible && (
        <BrokerFallbackModal
          isLoading={loading}
          isOpen={isFallbackModalVisible}
          onClose={closeFallbackModal}
          onRetry={handleRetryClick}
        />
      )}
    </NewbuildingBrokerWidgetContext.Provider>
  );
};
